import type { VisitorSummary } from '@livechat/shared';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export function VisitorActionsMenu({
	visitor,
	name,
	onCobrowse,
	onStartChat,
}: {
	visitor: VisitorSummary;
	name: string;
	onCobrowse: () => void;
	onStartChat: () => void;
}) {
	const button = useRef<HTMLButtonElement>(null);
	const menu = useRef<HTMLDivElement>(null);
	const [position, setPosition] = useState<{ top: number; left: number }>();
	useEffect(() => {
		if (!position) return;
		menu.current?.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus();
		const dismiss = (event: Event) => {
			if (
				!menu.current?.contains(event.target as Node) &&
				!button.current?.contains(event.target as Node)
			)
				setPosition(undefined);
		};
		const moved = () => setPosition(undefined);
		document.addEventListener('pointerdown', dismiss);
		document.addEventListener('focusin', dismiss);
		window.addEventListener('scroll', moved, true);
		window.addEventListener('resize', moved);
		return () => {
			document.removeEventListener('pointerdown', dismiss);
			document.removeEventListener('focusin', dismiss);
			window.removeEventListener('scroll', moved, true);
			window.removeEventListener('resize', moved);
		};
	}, [position]);
	const close = () => {
		setPosition(undefined);
		button.current?.focus();
	};
	const show = () => {
		const rect = button.current!.getBoundingClientRect();
		setPosition({
			top: Math.max(8, Math.min(rect.bottom + 7, window.innerHeight - 110)),
			left: Math.max(8, Math.min(rect.right - 190, window.innerWidth - 198)),
		});
	};
	return (
		<>
			<button
				aria-label={`Actions for ${name}`}
				aria-haspopup="menu"
				aria-expanded={Boolean(position)}
				className="conversation-more-button visitor-actions-button"
				ref={button}
				type="button"
				onClick={() => (position ? close() : show())}
				onKeyDown={(event) => {
					if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
						event.preventDefault();
						show();
					}
				}}
			>
				<svg aria-hidden="true" viewBox="0 0 24 24">
					<circle cx="12" cy="5" r="1.8" />
					<circle cx="12" cy="12" r="1.8" />
					<circle cx="12" cy="19" r="1.8" />
				</svg>
			</button>
			{position &&
				createPortal(
					<div
						aria-label={`Actions for ${name}`}
						className="conversation-more-menu visitor-actions-menu"
						ref={menu}
						role="menu"
						style={position}
						onKeyDown={(event) => {
							if (event.key === 'Escape') {
								event.preventDefault();
								close();
							}
							if (event.key === 'Tab') close();
							const items = Array.from(
								menu.current!.querySelectorAll<HTMLButtonElement>(
									'button:not(:disabled)',
								),
							);
							const index = items.indexOf(
								document.activeElement as HTMLButtonElement,
							);
							if (
								items.length &&
								['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)
							) {
								event.preventDefault();
								items[
									event.key === 'Home'
										? 0
										: event.key === 'End'
											? items.length - 1
											: (index +
													(event.key === 'ArrowUp' ? -1 : 1) +
													items.length) %
												items.length
								]!.focus();
							}
						}}
					>
						<button
							type="button"
							role="menuitem"
							disabled={!visitor.cobrowseEnabled || visitor.status === 'offline'}
							title={
								!visitor.cobrowseEnabled
									? 'Cobrowse is disabled for this integration'
									: visitor.status === 'offline'
										? 'Visitor is offline'
										: 'View this visitor’s webpage'
							}
							onClick={() => {
								close();
								onCobrowse();
							}}
						>
							Cobrowse
						</button>
						<button
							type="button"
							role="menuitem"
							disabled={visitor.status !== 'active' && !visitor.currentConversationId}
							title={
								visitor.currentConversationId
									? 'Open the current chat'
									: visitor.status === 'offline'
										? 'Visitor is offline'
										: undefined
							}
							onClick={() => {
								close();
								onStartChat();
							}}
						>
							Start Chat
						</button>
					</div>,
					document.body,
				)}
		</>
	);
}
